<template>
  <section class="before-you-go-page">
    <header class="before-you-go-page__header">
      <h1 class="before-you-go-page__header-title">Unsubscribe</h1>
      <p class="before-you-go-page__header-welcome">
        Welcome
        <span class="before-you-go-page__header-welcome-name">
          {{ authUser.first_name }}
        </span>
      </p>
    </header>
    <article class="before-you-go-page__body">
      <h2 class="before-you-go-page__body-heading">
        Before you go, consider these special offers:
      </h2>
      <section class="before-you-go-page__deck">
        <div class="before-you-go-page__card">
          <header class="before-you-go-page__card-header">
            <h2 class="before-you-go-page__card-heading">
              Switch To Annual For A Discounted Price
            </h2>
          </header>
          <div class="before-you-go-page__card-banner">
            <div class="before-you-go-page__card-banner-flag">MOST POPULAR</div>
          </div>
          <div class="before-you-go-page__card-body">
            <ul class="before-you-go-page__card-list">
              <li class="before-you-go-page__card-list-item">
                Switch to an annual plan and recieve your new 1-year
                subscription for ${{
                  product.unsubscribe_promo_price.replace('.00', '')
                }}
              </li>
              <li class="before-you-go-page__card-list-item">
                ${{ annualProduct.price.replace('.00', '') }} for following
                years
              </li>
            </ul>
          </div>
          <footer class="before-you-go-page__card-footer">
            <BaseButton
              :disabled="authUser.meta.has_used_btn_annual_promo"
              class="before-you-go-page__card-footer-button"
              @click="switchToAnnual"
            >
              PURCHASE ANNUAL
            </BaseButton>
          </footer>
        </div>
        <div class="before-you-go-page__card">
          <header class="before-you-go-page__card-header">
            <h2 class="before-you-go-page__card-heading">
              Temporarily Pause Your Subscription
            </h2>
          </header>
          <div class="before-you-go-page__card-body">
            <ul class="before-you-go-page__card-list">
              <li class="before-you-go-page__card-list-item">
                Pause subscription for 30 or 60 days
              </li>
              <li class="before-you-go-page__card-list-item">
                You will not be billed while subscription is paused
              </li>
              <li class="before-you-go-page__card-list-item">
                Regular monthly billing resumes automatically when the paused
                period is over
              </li>
            </ul>
          </div>
          <footer class="before-you-go-page__card-footer">
            <BaseButton
              class="before-you-go-page__card-footer-button before-you-go-page__card-footer-button--lighter-green before-you-go-page__card-footer-button--small"
              @click="pause_subscription(30)"
            >
              30-DAYS
            </BaseButton>
            <BaseButton
              class="before-you-go-page__card-footer-button before-you-go-page__card-footer-button--small"
              @click="pause_subscription(60)"
            >
              60-DAYS
            </BaseButton>
          </footer>
        </div>
        <div class="before-you-go-page__card">
          <header class="before-you-go-page__card-header">
            <h2 class="before-you-go-page__card-heading">
              Sign Up For Free Economic Updates
            </h2>
          </header>
          <div class="before-you-go-page__card-body">
            <ul class="before-you-go-page__card-list">
              <li class="before-you-go-page__card-list-item">
                Unsubscribe from By the Numbers subscription
              </li>
              <li class="before-you-go-page__card-list-item">
                Sign up for email updates on the latest economic trends,
                important insights, upcoming webinars, and more
              </li>
            </ul>
            <BaseCheckbox
              v-model="agreeToReceiveOtherCommunications"
              class="before-you-go-page__card-checkbox"
            >
              <span class="before-you-go-page__card-checkbox-text">
                I agree to receive other communications from ITR Economics.
              </span>
              <BaseTooltip>
                <template #display>
                  <img
                    src="/images/question-mark-gray.png"
                    class="before-you-go-page__card-question-mark"
                  />
                </template>
                <template #tooltip>
                  <p>
                    ITR Economics is committed to protecting your privacy.
                    Please review our privacy policy to learn more. By checking
                    this box, you consent to our contacting you and to our
                    storing and processing the personal information you have
                    entered above.
                  </p>
                </template>
              </BaseTooltip>
            </BaseCheckbox>
          </div>
          <footer class="before-you-go-page__card-footer">
            <BaseButton
              :disabled="!agreeToReceiveOtherCommunications"
              class="before-you-go-page__card-footer-button before-you-go-page__card-footer-button--small"
              @click="signUpForEconomicUpdates"
            >
              SIGN UP
            </BaseButton>
          </footer>
        </div>
      </section>
    </article>
    <footer class="before-you-go-page__footer">
      <a href="javascript:void(0)" @click="unsubscribe()">
        No, thank you. Please cancel my subscription. &#8594;
      </a>
    </footer>
  </section>
  <ItrLoading v-if="isSubmitting" infinite />
</template>

<script>
  import BaseCheckbox from '~/components/base/BaseCheckbox.vue';
  import BaseTooltip from '~/components/base/BaseTooltip.vue';
  import BaseButton from '~/components/base/BaseButton.vue';
  import ItrLoading from '~/components/ItrLoading.vue';

  export default {
    name: 'DashboardBeforeYouGoPage',
    components: {
      BaseCheckbox,
      BaseTooltip,
      BaseButton,
      ItrLoading,
    },
    props: {
      authUser: {
        type: Object,
        required: true,
      },
      product: {
        type: Object,
        required: true,
      },
      annualProduct: {
        type: Object,
        required: true,
      },
    },
    data: () => ({
      agreeToReceiveOtherCommunications: false,
      isSubmitting: false,
    }),
    methods: {
      async switchToAnnual() {
        if (this.authUser.meta.has_used_btn_annual_promo) return;

        try {
          this.isSubmitting = true;
          await axios.post(
            `/dashboard/cancelation/options/${this.product.url}`
          );
          window.location = `/dashboard/${this.product.url}-annual-subscription`;
        } catch (error) {
          // TODO: HANDLE ERROR - This is just silencing the error
          window.location.href = '/dashboard/contact-customer-care-error';
        } finally {
          this.isSubmitting = false;
        }
      },
      async unsubscribe() {
        const formData = new FormData();
        formData.append('product', this.product.url);
        try {
          this.isSubmitting = true;
          await axios.post(
            `/dashboard/unsubscribe/${this.product.url}`,
            formData
          );
          window.location = `/dashboard/unsubscribe/${this.product.url}?result=success`;
        } catch (error) {
          window.location = `/dashboard/unsubscribe/${this.product.url}?result=failure`;
        } finally {
          this.isSubmitting = false;
        }
      },
      async pause_subscription(numberOfDays) {
        try {
          this.isSubmitting = true;
          await axios.post(
            `/dashboard/cancelation/options/pause/${this.product.url}/${numberOfDays}`
          );
          window.location = `/dashboard/cancelation/options/pause/${this.product.url}/${numberOfDays}`;
        } catch (error) {
          // TODO: HANDLE ERROR - This is just silencing the error
        } finally {
          this.isSubmitting = false;
        }
      },
      async signUpForEconomicUpdates() {
        if (!this.agreeToReceiveOtherCommunications) return;

        const formData = new FormData();
        formData.append('product', this.product.url);
        try {
          this.isSubmitting = true;
          await axios.post(
            `/dashboard/unsubscribe/${this.product.url}`,
            formData
          );
          window.location = '/dashboard/newsletter-signup';
        } catch (error) {
          window.location = `/dashboard/unsubscribe/${this.product.url}?result=failure`;
        } finally {
          this.isSubmitting = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .before-you-go-page {
    padding: 30px 20px;

    @include screen-md {
      padding: 50px 40px;
    }

    @include screen-xl {
      padding: 70px 60px;
    }

    &__header {
      width: 100%;
      display: flex;
      flex-direction: column;
      font-size: 18px;
      margin-bottom: 70px;

      @include screen-md {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
      }
      @include screen-xl {
        margin-bottom: 20px;
      }

      &-title {
        color: #17355c;
        font-size: 40px;
        margin-right: 15px;
      }
      &-welcome {
        font-size: 20px;
        font-weight: bold;
        color: var(--itr-blue-mid);
        line-height: normal;

        &-name {
          font-style: italic;
          color: var(--itr-blue);
        }
      }
    }

    &__body {
      width: 100%;
      max-width: 420px;
      margin: 0 auto;
      margin-top: 40px;
      padding: 0 15px;

      @include screen-sm {
        max-width: 540px;
      }
      @include screen-md {
        max-width: 720px;
        margin-top: 50px;
      }
      @include screen-lg {
        max-width: 920px;
        margin-top: 60px;
      }
      @include screen-xl {
        max-width: 1100px;
      }
      @include screen-xxl {
        max-width: 1300px;
      }
      @include screen-max {
        max-width: 1480px;
        margin-top: 70px;
      }

      &-heading {
        color: #17355c;
        font-weight: 800;
        font-size: 24px;
        margin-right: 15px;
        margin-bottom: 30px;
      }
    }

    &__deck {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: clamp(12px, 5vw, 30px);

      @include screen-md {
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;
      }
    }
    &__card {
      display: flex;
      flex-direction: column;
      width: 100%;
      background-color: #f1f1f1;

      border: 4px solid #f0f1f1;
      box-sizing: border-box;
      border-radius: 12px;

      @include screen-md {
        width: 33%;
      }
      @include screen-md {
        height: 90vh;
        max-height: 750px;
      }
      @include screen-xl {
        height: 90vh;
        max-height: 650px;
      }
      @include screen-max {
        height: 90vh;
        max-height: 530px;
      }

      &-heading {
        color: #09a684;
        font-size: 18px;
        font-weight: 700;
        margin: 0;
        padding: 0;
      }

      &-header {
        background-color: var(--itr-white);
        padding: 35px 20px;
        text-align: center;
        height: fit-content;
        min-height: 60px;

        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
      }

      &-banner {
        position: relative;
        width: 100%;
        height: 37px;

        &-flag {
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          color: #fff;
          background-color: #2b74a7;
          padding: 6px;
          font-weight: 500;
          font-size: 20px;
          text-align: center;
          height: fit-content;

          width: 108%;

          @include screen-lg {
            width: 112%;
          }
          @include screen-xl {
            width: 108%;
          }
          @include screen-xxl {
            width: 106%;
          }

          &::before {
            position: absolute;
            top: -0.5em;
            left: 0;
            content: '';
            height: 0.5em;
            width: 0.5em;
            border-bottom: solid 0.25em #183962;
            border-right: solid 0.25em #183962;
            border-left: solid 0.25em transparent;
            border-top: solid 0.25em transparent;
          }

          &::after {
            position: absolute;
            top: -0.5em;
            right: 0;
            content: '';
            height: 0.5em;
            width: 0.5em;
            border-bottom: solid 0.25em #183962;
            border-left: solid 0.25em #183962;
            border-right: solid 0.25em transparent;
            border-top: solid 0.25em transparent;
          }
        }
      }

      &-body {
        flex: auto 1 1;
        display: flex;
        flex-direction: column;
        padding: 32px 14px 32px 0;
        gap: 10px;
        overflow: hidden;
      }

      &-list {
        display: flex;
        flex-direction: column;
        gap: 32px;

        &-item {
          &::marker {
            color: #65a8d2;
          }
        }
      }

      &-checkbox {
        margin: 32px 0 0 32px;

        &-text {
          font-size: 14px;
          font-weight: 500;
          font-style: italic;
          line-height: 1;
        }
      }

      &-footer {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
        padding: 22px 10px;
        background-color: var(--itr-white);

        @include screen-xl {
          padding: 32px 15px;
          flex-direction: row;
          justify-content: center;
          gap: 30px;
        }

        &-button {
          padding: 8px 24px;
          width: fit-content;

          &--lighter-green {
            background-color: #59b6a4;

            &:hover {
              background-color: #50a393;
            }
          }
        }
      }

      &-question-mark {
        width: 18px;
        height: auto;
      }
    }

    &__footer {
      width: 100%;
      display: flex;
      justify-content: center;
      padding: 56px 6px;

      a {
        font-size: 18px;
        font-weight: 500;
        color: var(--itr-blue);
        text-decoration: underline;
      }
    }
  }

  :deep(.base-button:disabled),
  :deep(.base-button:hover:disabled) {
    color: #ffffff;
  }

  :deep(.base-checkbox svg .box) {
    fill: #ffffff;
  }

  :deep(.base-tooltip__display) {
    height: fit-content;
    vertical-align: middle;
  }
</style>
